'use client';

import { CSSProperties, KeyboardEvent, ReactNode, useCallback, useEffect, useId, useRef, useState } from 'react';

import { css } from 'styled-system/css';

type Item = {
  /** Tab label (icon + title), server-rendered. */
  tab: ReactNode;
  /** Panel content (media + caption), server-rendered. */
  panel: ReactNode;
};

type Props = {
  items: Item[];
  tablistLabel: string;
};

const LIME = '#CEF04B';
const LINE = 'rgba(255, 255, 255, 0.1)';
const EASE = 'cubic-bezier(.22,.61,.36,1)';

// Media | tabs, framed by 1px lines (the prototype's "style 3"): top-left corner 64px, the rest 4px.
const bodyClass = css({
  marginTop: { base: '40px', sm: '60px' },
  display: 'grid',
  gridTemplateColumns: { base: '1fr', md: 'minmax(0, 2fr) minmax(0, 1fr)' },
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: LINE,
  borderRadius: { base: '40px 4px 4px 4px', md: '64px 4px 4px 4px' },
});

const mediaClass = css({
  position: 'relative',
  // Two rows shared by every panel (subgrid): the scene, then the captions. The caption row is as
  // tall as the longest caption, so the scene sits in the same place on every tab.
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr)',
  // The scene row has a fixed height on phones/tablets (nothing in a scene can push the card);
  // from md it also fills the height of the tabs column next to it.
  gridTemplateRows: { base: 'var(--ai-scene-h) auto', md: 'minmax(var(--ai-scene-h), 1fr) auto' },
  '--ai-scene-h': { base: '252px', xs: '296px', md: '404px' },
  overflow: 'hidden',
  // What shows under the first panel while it wipes in on the first view (later panels wipe in
  // over the previous one): the illustrations' dark stage, so it doesn't flash grey.
  backgroundColor: '#0B0817',
  backgroundImage: 'linear-gradient(160deg, #1A1626 0%, #0B0817 100%)',
  borderRadius: { base: '39px 3px 0 0', md: '63px 0 0 3px' },
  borderColor: LINE,
  borderBottomWidth: { base: '1px', md: 0 },
  borderBottomStyle: 'solid',
  borderRightWidth: { base: 0, md: '1px' },
  borderRightStyle: 'solid',
});

// Panels stack over both rows of the media box and take its rows (subgrid), so the card keeps its
// height and the scene its place between tabs. Each panel: the scene on top, the caption under it
// (in flow, so any length fits); the stage's backdrop runs under both (a subgrid again). The incoming
// panel wipes in from the top over the previous one (which only dims), its media sharpens from a
// slight zoom and the caption rises in.
const panelClass = css({
  gridRow: '1 / -1',
  gridColumn: '1',
  margin: 0,
  position: 'relative',
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr)',
  gridTemplateRows: 'subgrid',
  overflow: 'hidden',
  clipPath: 'inset(0)',
  isolation: 'isolate',
  // Closed panels stay in the layout (not `hidden`: the reset's `[hidden] { display: none !important }`
  // would drop them and the card would resize to each tab's caption).
  '&[data-off]': { visibility: 'hidden' },
  '& > [data-ai-stage], & > [data-ai-media]': {
    position: 'relative',
    inset: 'auto',
    gridRow: '1 / -1',
    gridColumn: '1',
  },
  '& > [data-ai-stage]': { gridTemplateRows: 'subgrid' },
  // Darkens the bottom so the caption reads on any photo.
  _after: {
    content: '""',
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(0deg, rgba(6,3,15,.6) 0%, rgba(6,3,15,.22) 28%, rgba(6,3,15,0) 50%)',
    pointerEvents: 'none',
  },
  '&[data-state="in"]': { animation: 'sh-ai-wipe .9s cubic-bezier(.65,0,.35,1) both' },
  '&[data-state="in"] > [data-ai-media]': { animation: `sh-ai-media 1.1s ${EASE} both` },
  '&[data-state="in"] figcaption': {
    animation: `sh-hero-fade .35s ${EASE} .35s both, sh-hero-rise .8s ${EASE} .35s both`,
  },
  '&[data-state="out"]': { animation: 'sh-ai-dim .9s cubic-bezier(.65,0,.35,1) both' },
  _motionReduce: { '&, & *': { animation: 'none !important' } },
});

// White timer along the bottom of the media; when it fills, the next tab opens.
const barClass = css({
  position: 'absolute',
  left: 0,
  bottom: 0,
  width: '100%',
  height: '2px',
  backgroundColor: 'white',
  transform: 'scaleX(0)',
  transformOrigin: '0 50%',
  zIndex: 900,
  '&[data-run]': { animation: 'sh-ai-progress var(--ai-dur) linear forwards' },
});

const tabsClass = css({ display: 'flex', flexDirection: 'column' });

const tabClass = css({
  appearance: 'none',
  position: 'relative',
  flex: '1 1 0',
  minHeight: { base: '72px', md: '96px' },
  display: 'flex',
  alignItems: 'center',
  gap: { base: 4, xl: 6 },
  paddingX: { base: 5, xs: 7, xl: 10 },
  textAlign: 'left',
  cursor: 'pointer',
  border: 0,
  backgroundColor: 'transparent',
  color: 'rgba(255, 255, 255, 0.64)',
  fontSize: { base: '16px', xs: '18px', xl: '20px' },
  lineHeight: { base: '24px', xs: '28px', xl: '32px' },
  transition: `background-color .5s ${EASE}, color .5s ${EASE}`,
  '&:not(:first-child)': { borderTopWidth: '1px', borderTopStyle: 'solid', borderTopColor: LINE },
  '& svg': { flex: 'none', width: { base: '24px', sm: '32px' }, height: { base: '24px', sm: '32px' } },
  '& svg, & svg *': { transition: `color .5s ${EASE}` },
  // Accent line on the left of the active tab.
  _before: {
    content: '""',
    position: 'absolute',
    left: { base: 0, md: '-1px' },
    top: { base: '16px', md: '-1px' },
    bottom: { base: '16px', md: '-1px' },
    width: '2px',
    backgroundColor: LIME,
    transform: 'scaleY(0)',
    transformOrigin: { base: '50% 50%', md: '50% 0' },
    transition: 'transform .5s cubic-bezier(.65,0,.35,1)',
  },
  _hover: { backgroundColor: 'rgba(255, 255, 255, 0.03)', color: 'white' },
  '&[aria-selected="true"]': {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    color: 'white',
    '& svg': { color: LIME },
    _before: { transform: 'scaleY(1)' },
  },
  _focusVisible: { outline: `2px solid ${LIME}`, outlineOffset: '-3px' },
});

const DURATION_S = 5;

/**
 * Tabs + stacked panels of "How we use AI in Design" (UX Design prototype): a tab switches the panel; a white bar counts down and opens the next tab, in a loop, only while
 * the section is on screen. Rapid clicks are safe: every new panel goes on top of the stack and the
 * ones under it are dropped only when it has finished wiping in. Reduced motion: no timer, no
 * transitions. Without JS the first panel shows and every panel's copy is in the HTML.
 */
export const AiTabs = ({ items, tablistLabel }: Props) => {
  const id = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const activeRef = useRef(0);
  const [active, setActive] = useState(0);
  // Panels currently shown, bottom → top; the last one is the active panel.
  const [stack, setStack] = useState<number[]>([0]);
  const [entering, setEntering] = useState<number | null>(null);
  // Bumped to restart a panel's entry animation (remount) and the timer.
  const [panelRuns, setPanelRuns] = useState<number[]>(() => items.map(() => 0));
  const [timerRun, setTimerRun] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [reduceMotion, setReduceMotion] = useState(false);

  const select = useCallback(
    (index: number, focus = false) => {
      const next = (index + items.length) % items.length;
      setTimerRun((n) => n + 1);
      if (focus) tabRefs.current[next]?.focus();
      if (next === activeRef.current) return;
      activeRef.current = next;
      setActive(next);
      if (reduceMotion) {
        setStack([next]);
        setEntering(null);
        return;
      }
      setStack((s) => [...s.filter((i) => i !== next), next]);
      setEntering(next);
      setPanelRuns((runs) => runs.map((n, i) => (i === next ? n + 1 : n)));
    },
    [items.length, reduceMotion],
  );

  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduceMotion(query.matches);
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);

  // The timer only runs while a quarter of the section is on screen.
  useEffect(() => {
    const root = rootRef.current;
    if (!root || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(([entry]) => setIsVisible(entry.isIntersecting), { threshold: 0.25 });
    observer.observe(root);
    return () => observer.disconnect();
  }, []);

  // The first time the section comes into view, the open panel plays its entry too (its media
  // builds up), unless a tab was already picked.
  const hasPlayedRef = useRef(false);
  useEffect(() => {
    if (!isVisible || hasPlayedRef.current) return;
    hasPlayedRef.current = true;
    if (reduceMotion || activeRef.current !== 0) return;
    setEntering(0);
    setPanelRuns((runs) => runs.map((n, i) => (i === 0 ? n + 1 : n)));
  }, [isVisible, reduceMotion]);

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    const keys: Record<string, number> = {
      ArrowDown: active + 1,
      ArrowRight: active + 1,
      ArrowUp: active - 1,
      ArrowLeft: active - 1,
      Home: 0,
      End: items.length - 1,
    };
    if (!(event.key in keys)) return;
    event.preventDefault();
    select(keys[event.key], true);
  };

  const top = stack[stack.length - 1];

  return (
    <div ref={rootRef} className={bodyClass} style={{ '--ai-dur': `${DURATION_S}s` } as CSSProperties}>
      <div className={mediaClass}>
        {items.map((item, i) => {
          const depth = stack.indexOf(i);
          const state = i === top ? (entering === i ? 'in' : undefined) : depth >= 0 ? 'out' : undefined;
          return (
            <figure
              key={`${i}-${panelRuns[i]}`}
              id={`${id}-panel-${i}`}
              role="tabpanel"
              aria-labelledby={`${id}-tab-${i}`}
              data-off={depth < 0 ? '' : undefined}
              aria-hidden={depth < 0 || undefined}
              inert={depth < 0 || undefined}
              data-state={state}
              className={panelClass}
              style={depth >= 0 ? { zIndex: depth + 1 } : undefined}
              onAnimationEnd={(event) => {
                if (event.target !== event.currentTarget || event.animationName !== 'sh-ai-wipe') return;
                // Panels underneath are dropped; this one keeps `data-state="in"` so its media can
                // finish animating (the illustrations run longer than the wipe).
                setStack((s) => (s[s.length - 1] === i ? [i] : s));
              }}
            >
              {item.panel}
            </figure>
          );
        })}
        <span
          key={timerRun}
          aria-hidden="true"
          className={barClass}
          data-run={reduceMotion ? undefined : ''}
          style={{ animationPlayState: isVisible ? 'running' : 'paused' }}
          onAnimationEnd={() => select(activeRef.current + 1)}
        />
      </div>
      <div className={tabsClass} role="tablist" aria-label={tablistLabel} aria-orientation="vertical">
        {items.map((item, i) => (
          <button
            key={i}
            ref={(el) => {
              tabRefs.current[i] = el;
            }}
            type="button"
            role="tab"
            id={`${id}-tab-${i}`}
            aria-controls={`${id}-panel-${i}`}
            aria-selected={i === active}
            tabIndex={i === active ? 0 : -1}
            className={tabClass}
            onClick={() => select(i)}
            onKeyDown={onKeyDown}
          >
            {item.tab}
          </button>
        ))}
      </div>
    </div>
  );
};
